import React, { useEffect, useState } from 'react';
import { Alert, Button, Dropdown, Image, Nav, Navbar } from 'react-bootstrap';

import {
  LayoutDashboard,
  ClipboardList,
  User,
  LogOut,
  FileText,
  CheckSquare,
  Search,
  PlusCircle,
  Settings,
  Users,
  Clock,
  Receipt,
} from 'lucide-react';

import { useHistory, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../helpers/api';
import {
  ADMIN_PAGES_VIEW_ACCORDING_TO_ROLE,
  ENDPOINTS,
  ROLES_TO_WHICH_ADMIN_SECTION_IS_NOT_VISIBLE
} from '../helpers/constants';
import { isClient, isSalesPerson, isStaff, logout } from '../helpers/global';
import AddEditServiceSidebar from './services/pipeline/AddEditServiceSidebar';
import Turnaround from './Turnaround/Turnaround';
import NotificationBell from './notifications/NotificationBell';

// Custom Toggle for the dropdown menu -- to hide the toggle arrow icon
const CustomToggle = ({ children, onClick }) => (
  <div
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
    style={{ cursor: 'pointer', padding: '0px', border: 'none', background: 'transparent' }}
  >
    {children}
  </div>
);

const DropDownItems = ({ history, currentUserInfo }) => {
  const name = currentUserInfo?.name || '';
  const userInitials =
    name
      .split(' ')
      .slice(0, 2)
      .map(n => n[0])
      .join('')
      .toUpperCase() || 'U';
  const userEmail = currentUserInfo?.email || '';

  return (
    <>
      <Dropdown.Toggle
        as={CustomToggle}
        size="sm"
        variant="light"
        style={{ padding: 0, border: 'none', background: 'transparent' }}
      >
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            height: 40,
            width: 40,
            borderRadius: '9999px',
            backgroundColor: '#FACC14',
            color: 'black',
            fontWeight: 600
          }}
        >
          <span>{userInitials}</span>
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu
        align="end"
        style={{
          position: 'absolute',
          top: '105%',
          right: '0',
          zIndex: 1000,
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          padding: '8px 0',
          minWidth: '200px'
        }}
      >
        <div style={{ padding: '4px 12px' }}>
          <div className="d-flex flex-column">
            <p className="mb-1" style={{ fontSize: '14px', fontWeight: 600, lineHeight: 1 }}>
              {currentUserInfo?.role}
            </p>
            <p className="mb-0 text-muted" style={{ fontSize: '12px', lineHeight: 1 }}>
              {userEmail}
            </p>
          </div>
        </div>
        <Dropdown.Divider style={{ margin: '3px' }} />
        <Dropdown.Item
          as={Link}
          to="/profile"
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            color: '#495057',
            transition: 'all 0.2s ease',
            textDecoration: 'none'
          }}
          onMouseEnter={e => (e.target.style.backgroundColor = '#f1f1f1')}
          onMouseLeave={e => (e.target.style.backgroundColor = 'transparent')}
        >
          <User size={18} className="align-text-top mr-2" />
          <span>Profile</span>
        </Dropdown.Item>
        <Dropdown.Divider style={{ margin: '3px' }} />
        <Dropdown.Item
          onClick={() => logout(history)}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            color: '#dc3545',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={e => (e.target.style.backgroundColor = '#fff5f5')}
          onMouseLeave={e => (e.target.style.backgroundColor = 'transparent')}
        >
          <LogOut size={20} className="align-text-top mr-2" />
          <span>Log out</span>
        </Dropdown.Item>
      </Dropdown.Menu>
    </>
  );
};

// utils functions
const isActiveLink = path => `${location.pathname}` === path;

const navItemStyle = (isActive, extraStyles = {}) => ({
  fontSize: '15px',
  border: 'none',
  boxShadow: 'none',
  borderRadius: '12px',
  fontWeight: 500,
  ...(isActive
    ? {
        backgroundColor: '#FACC15',
        color: '#0f172a'
      }
    : {
        color: '#0f172a',
        backgroundColor: 'transparent'
      }),
  ...extraStyles
});

const Header = ({ currentUserInfo, onServiceCreated }) => {
  const history = useHistory();
  const location = useLocation();

  const [role] = useState(localStorage.getItem('user-role'));
  const [pendingCount, setPendingCount] = useState(0);
  const [addServiceMeta, setAddServiceMeta] = useState(null);
  const [createdService, setCreatedService] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  const [expanded, setExpanded] = useState(false);

  // event listener for viewport width
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isTabWidth = viewportWidth >= 990 && viewportWidth <= 1220;

  const LinkItem = ({ title, path, icon: Icon }) => {
    const isActive = isActiveLink(path);

    return (
      <Link
        to={path}
        className="d-flex align-items-center"
        style={{
          ...navItemStyle(isActive, {
            padding: '0 12px',
            height: '36px'
          }),
          textDecoration: 'none',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={e => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.06)';
          }
        }}
        onMouseLeave={e => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        {Icon && <Icon size={16} className="mr-2 align-text-top" />}
        <span>{title}</span>
      </Link>
    );
  };

  const getPendingOrdersCount = async () => {
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.PENDING_ORDERS_COUNT,
      method: 'GET'
    });

    if (error) {
      toast.error(error);
      return;
    }
    setPendingCount(response?.count || 0);
  };

  useEffect(() => {
    if (isStaff(role) && !isSalesPerson(role)) getPendingOrdersCount();
  }, []);

  useEffect(() => {
    if (isSalesPerson(role)) return;
    const params = new URLSearchParams(location.search);
    if (params.get('newOrder') !== '1') return;
    setAddServiceMeta({});
    params.delete('newOrder');
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
      hash: location.hash
    });
  }, [location.pathname, location.search, location.hash, role, history]);

  useEffect(() => {
    if (createdService && onServiceCreated) {
      onServiceCreated('newOrder', createdService);
      setCreatedService(null);
    }
  }, [createdService, onServiceCreated]);

  const onServiceAdd = service => {
    setCreatedService(service);
    setAddServiceMeta(null);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
      <Navbar
        expand="lg"
        expanded={expanded}
        onToggle={setExpanded}
        className="py-2"
        style={{
          backgroundColor: 'white',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Navbar.Brand
          href={isClient(role) ? '/org-dashboard' : '/services/pipeline'}
          className="d-flex align-items-center flex-shrink-0"
          style={{ flexShrink: 0 }}
        >
          <Image src={`${process.env.PUBLIC_URL}/logo.png`} width={140} height={38} />
          {isClient(role) && (
            <>
              <div className="bg-dark ml-1 mr-2" style={{ width: '2px', height: '20px' }} />
              <div className="text-truncate largeFont font-weight-bold" style={{ maxWidth: '200px' }}>
                {currentUserInfo?.organization?.name}
              </div>
            </>
          )}
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav
            className={`${
              expanded
                ? 'mx-auto d-flex align-items-left gap-lg-0'
                : 'd-flex position-absolute mx-auto d-flex align-items-left gap-lg-0'
            }`}
            style={{
              gap: isTabWidth ? '0px' : '4px',
              ...(!expanded && { left: '50%', transform: 'translateX(-50%)' })
            }}
          >
            {/* Client Dashboard */}
            {isClient(role) && <LinkItem path="/org-dashboard" title="Dashboard" icon={LayoutDashboard} />}

            {isStaff(role) && <LinkItem path="/dashboard" title="Dashboard" icon={LayoutDashboard} />}

            {!isSalesPerson(role) && (
              <LinkItem
                path="/services/pipeline"
                title={role !== 'Client' ? (isTabWidth ? 'Orders' : 'All Orders') : 'Company Orders'}
                icon={FileText}
              />
            )}

            {isClient(role) && <LinkItem path="/statement" title="Statement" icon={Receipt} />}
            {!isClient(role) && !isSalesPerson(role) && (
              <>
                <LinkItem path="/tasks" title="Tasks" icon={CheckSquare} />
              </>
            )}
            {!isSalesPerson(role) && (
              <>
                <LinkItem path="/hoa/explorer" title={isTabWidth ? 'HOAs' : 'HOA Explorer'} icon={Search} />

                <Turnaround
                  title={isClient(role) ? 'LIVE Turnarounds' : 'Turnarounds'}
                  icon={Clock}
                  style={navItemStyle(`${location.pathname}` === '/turnaround-times', {
                    padding: '0 12px',
                    width: 'fit-content',
                    height: '36px'
                  })}
                  onMouseEnter={e => {
                    if (`${location.pathname}` !== '/turnaround-times') {
                      e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.06)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (`${location.pathname}` !== '/turnaround-times') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                />
              </>
            )}

            {!ROLES_TO_WHICH_ADMIN_SECTION_IS_NOT_VISIBLE.includes(role) &&
              (ADMIN_PAGES_VIEW_ACCORDING_TO_ROLE[role]?.labels?.length === 1 ? (
                // Single admin page - show as direct LinkItem
                <LinkItem
                  path={`/admin/${ADMIN_PAGES_VIEW_ACCORDING_TO_ROLE[role]?.links[0]}`}
                  title={ADMIN_PAGES_VIEW_ACCORDING_TO_ROLE[role]?.labels[0]}
                  icon={ADMIN_PAGES_VIEW_ACCORDING_TO_ROLE[role]?.links[0] === 'users' ? Users : null}
                />
              ) : (
                // Multiple admin pages - show in NavDropdown
                <Dropdown
                  show={showAdmin}
                  className="ml-0"
                  onMouseEnter={() => setShowAdmin(true)}
                  onMouseLeave={() => setShowAdmin(false)}
                  onClick={() => setShowAdmin(prev => !prev)}
                >
                  <Dropdown.Toggle
                    className="d-flex align-items-center"
                    style={navItemStyle(`${location.pathname}`.startsWith('/admin'))}
                    onMouseEnter={e => {
                      if (!`${location.pathname}`.startsWith('/admin')) {
                        e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.06)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!`${location.pathname}`.startsWith('/admin')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Settings size={16} className="mr-2 align-text-top" />
                    Admin
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="start" style={{ padding: '4px', width: 'fit-content' }}>
                    {ADMIN_PAGES_VIEW_ACCORDING_TO_ROLE[role]?.labels.map((r, index) => {
                      if (r === 'Financial Intelligence' && !currentUserInfo?.hasFinancialIntelligenceAccess) {
                        return null;
                      }
                      const linkKey = ADMIN_PAGES_VIEW_ACCORDING_TO_ROLE[role]?.links[index];
                      const adminPath = `/admin/${linkKey}`;
                      const isActiveItem =
                        `${location.pathname}` === adminPath ||
                        (linkKey === 'back-office' && `${location.pathname}`.startsWith('/admin/back-office'));
                      return (
                        <Dropdown.Item
                          key={`${r}-${index}`}
                          as={Link}
                          to={adminPath}
                          style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            color: isActiveItem ? '#0f172a' : '#495057',
                            backgroundColor: isActiveItem ? '#FACC15' : 'transparent',
                            borderRadius: '6px',
                            margin: '2px 0',
                            textDecoration: 'none'
                          }}
                          onMouseEnter={e => {
                            if (!isActiveItem) e.target.style.backgroundColor = '#f1f1f1';
                          }}
                          onMouseLeave={e => {
                            if (!isActiveItem) e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          {r}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              ))}
          </Nav>

          <div className={`${!expanded && 'd-flex align-items-center ml-auto justify-content-end'}`} style={{ gap: 8 }}>
            {!isSalesPerson(role) && (
              <Button className="d-flex mt-2 mt-lg-0" variant="success" onClick={() => setAddServiceMeta({})}>
                <span>
                  <PlusCircle className="mr-2 align-text-top" size={16} />
                </span>
                <span>{isTabWidth ? 'Order' : 'New Order'}</span>
              </Button>
            )}

            <div className={`d-flex align-items-center mt-2 mt-lg-0`} style={{ gap: 16 }}>
              {(isStaff(role) || (!isSalesPerson(role) && pendingCount > 0)) && (
                <NotificationBell
                  notificationsEnabled={isStaff(role)}
                  {...(pendingCount > 0 && !isSalesPerson(role) && { pendingOrdersCount: pendingCount })}
                >
                  {!isSalesPerson(role) && pendingCount > 0 && (
                    <Alert
                      as={Link}
                      to="/services/pipeline?orderStatus=Pending"
                      className="px-2 py-1 d-flex align-items-center mb-0 border-dark text-dark smallFont hover w-100"
                      variant="primary-light-2"
                      role="button"
                      title="View pending orders in pipeline"
                      style={{ textDecoration: 'none' }}
                    >
                      <ClipboardList className="mr-1 text-warning" size={15} aria-hidden />
                      <span className="font-weight-bold mx-1">{pendingCount}</span>
                      Pending Orders
                    </Alert>
                  )}
                </NotificationBell>
              )}
              <Dropdown style={{ zIndex: 999 }} drop={`${expanded ? 'down' : 'left'}`}>
                <DropDownItems history={history} currentUserInfo={currentUserInfo} />
              </Dropdown>
            </div>
          </div>
        </Navbar.Collapse>
      </Navbar>

      <AddEditServiceSidebar
        onServiceAddUpdate={onServiceAdd}
        show={addServiceMeta !== null}
        editingService={addServiceMeta}
        onHide={() => setAddServiceMeta(null)}
      />
    </div>
  );
};

export default Header;
